import * as React from 'react';
import { useDiagnosisPermission } from '../../../contexts/DiagnosisPermissionContext';
import { forwardRef, useState, useMemo, useRef } from 'react';
import { Stack, Typography, Button, Box, Checkbox } from '@mui/material';
import toast from 'react-hot-toast';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import { ColumnMappingDialog, type ColumnMappingField } from '../../common/ColumnMappingDialog';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import {
  CrossEntityDateCrudDataGrid,
  ValueWithEntityReference,
  CrossEntityDateCrudDataGridHandle,
  ValuePropConfig,
  reconcileColumnOrder,
} from '../../CrossEntityDateCrudDataGrid';
import dayjs from 'dayjs';
import {
  DiagnosisMetersReadDTO,
  DiagnosisReadDTO,
  MetersDataReadDTO,
  MetersDataCreateDTO,
  EnergyVectorsReadDTO,
  MeterType,
} from '../../../generated/api-client';
import { DiagnosisStepHandle } from './Step.types';
import { apiClient, axiosInstance } from '../../../services/apiClient';
import { getAxiosErrorDetail } from '../../../utils/axiosErrorDetail';
import { toYyyyMmDd } from '../../../utils/date.utils';
import { EditMeterDialog } from './EditMeterDialog';
import { GridRowId } from '@mui/x-data-grid';
import { useVectorTypeOptions } from '../../../hooks/useVectorTypeOptions';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import { ActionSpacer } from '../../common';
import { getFunctionalAreaLabel } from '../../../utils/functionalArea';
import { formatUnitOfMeasurement } from '../../../utils/unitOfMeasurementFormatter';
import {
  buildReferencePeriodYearlyValues,
  type SourceValueItem,
} from '../../../utils/referencePeriodYearlyValues';
import {
  useBatchSaveMetersData,
  useDiagnosisMetersByDiagnosisId,
  useDiagnosisEnergyVectorsByDiagnosisId,
  useDiagnosisEnergyUseByDiagnosisId,
  invalidateConsumptionTotalsQueries,
} from '../../../hooks';

interface DiagnosisMetersStepProps {
  diagnosisId?: string;
  diagnosis?: DiagnosisReadDTO | null;
}

export const DiagnosisMetersStep = React.memo(
  forwardRef<DiagnosisStepHandle, DiagnosisMetersStepProps>(({ diagnosisId, diagnosis }, ref) => {
    const { readOnly } = useDiagnosisPermission();
    const queryClient = useQueryClient();
    const { data: vectorTypeOptions = [] } = useVectorTypeOptions();
    const crossEntityGridRef = useRef<CrossEntityDateCrudDataGridHandle>(null);
    const crossEntityGridRefAnnual = useRef<CrossEntityDateCrudDataGridHandle>(null);
    const crossEntityGridRefPeriodoRiferimento = useRef<CrossEntityDateCrudDataGridHandle>(null);

    // Ordine colonne (ID contatori): condiviso fra la griglia mensile e le due tabelle annue.
    // Essendo passato come prop "controlled" a tutte e tre le griglie, lo spostamento di una
    // colonna (possibile solo dalla griglia mensile) si riflette subito anche nelle tabelle
    // annue, prima ancora del salvataggio.
    const [entityOrder, setEntityOrder] = useState<string[]>([]);

    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedMeter, setSelectedMeter] = useState<DiagnosisMetersReadDTO | null>(null);
    const [selectedMeterIds, setSelectedMeterIds] = useState<string[]>([]);
    const [bulkDeleteDialog, setBulkDeleteDialog] = useState<string[] | null>(null);
    const [pasteMappingDialog, setPasteMappingDialog] = useState<{
      open: boolean;
      clipboardData: string[][];
    }>({ open: false, clipboardData: [] });

    const { data: allMeters = [], isLoading } = useDiagnosisMetersByDiagnosisId(diagnosisId!);

    // Filtra i contatori fake (is_fake === true non vengono mostrati)
    const meters = React.useMemo(() => {
      return allMeters.filter(m => !m.is_fake);
    }, [allMeters]);

    // Chiave stabile per sincronizzare l'effetto di riconciliazione solo quando cambia
    // davvero l'elenco dei contatori (non ad ogni refetch con stesso contenuto).
    const metersIdsKey = useMemo(
      () => meters.map(m => m.id).filter(Boolean).join('|'),
      [meters]
    );

    // Riconcilia l'ordine colonne quando cambia l'elenco dei contatori (nuovo contatore
    // creato, contatore eliminato), mantenendo l'ordine scelto dall'utente per quelli
    // ancora presenti. Il "default order" è l'ordine attuale lato backend (campo `index`).
    React.useEffect(() => {
      const defaultOrder = meters.map(m => m.id!).filter(Boolean);
      setEntityOrder(prev => {
        const reconciled = reconcileColumnOrder(prev, defaultOrder);
        const unchanged =
          reconciled.length === prev.length && reconciled.every((id, i) => id === prev[i]);
        return unchanged ? prev : reconciled;
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [metersIdsKey]);

    // Field names (`entity-{id}`) per le prop "controlled" delle 3 griglie
    const columnOrderFields = useMemo(() => entityOrder.map(id => `entity-${id}`), [entityOrder]);
    const handleColumnOrderChange = React.useCallback((fields: string[]) => {
      setEntityOrder(fields.map(f => f.replace('entity-', '')));
    }, []);

    // Carica i vettori energetici della diagnosi
    const { data: energyVectors = [] } = useDiagnosisEnergyVectorsByDiagnosisId(diagnosisId!);

    // Carica i vettori energetici generici per ottenere unit_of_measurement e nome
    const { data: genericEnergyVectors = [] } = useQuery<EnergyVectorsReadDTO[]>(
      'generic-energy-vectors',
      () => apiClient.list_items_energy_vectors__get(),
      { enabled: true }
    );

    // Carica gli usi energetici della diagnosi
    const { data: energyUses = [] } = useDiagnosisEnergyUseByDiagnosisId(diagnosisId);

    // Carica tutti i MetersData per tutti i contatori della diagnosi
    const { data: allMetersData = [], isLoading: isLoadingMetersData } = useQuery<
      MetersDataReadDTO[]
    >(
      [`all-meters-data-${diagnosisId}`, meters.map(m => m.id)],
      async () => {
        if (!meters.length) return [];
        // Carica i dati per ogni contatore
        const allData: MetersDataReadDTO[] = [];

        for (const meter of meters) {
          if (meter.id) {
            const data =
              await apiClient.search_by_meter_id_meters_data_search_by_meter_id__meter_id__get(
                meter.id
              );
            allData.push(...data);
          }
        }
        return allData;
      },
      { enabled: !!diagnosisId && meters.length > 0 }
    );

    // Batch save mutation for meters data
    const batchSaveMetersData = useBatchSaveMetersData();

    // Mutation for updating meter order
    const updateOrderMutation = useMutation(
      async (meterIds: string[]) => {
        try {
          const { data } = await axiosInstance.post('/diagnosis-meters/update_order', {
            meter_ids: meterIds,
          });
          return data;
        } catch (e: unknown) {
          throw new Error(getAxiosErrorDetail(e, "Errore nel salvataggio dell'ordinamento"));
        }
      },
      {
        onSuccess: () => {
          // L'ordine locale (entityOrder) è già quello inviato: non serve risincronizzarlo qui,
          // basta invalidare la query per riflettere l'`index` aggiornato lato backend.
          queryClient.invalidateQueries(`diagnosis-meters-${diagnosisId}`);
          toast.success('Ordinamento contatori salvato con successo');
        },
        onError: (error: any) => {
          toast.error(error?.message || "Errore nel salvataggio dell'ordinamento dei contatori");
        },
      }
    );

    const batchSaveMutation = useMutation(
      async (itemsToSave: Array<MetersDataCreateDTO & { id?: string }>) => {
        // Salva tutti i dati modificati in una sola chiamata batch
        if (itemsToSave.length > 0) {
          await batchSaveMetersData.mutateAsync(itemsToSave);
        }
      },
      {
        onSuccess: results => {
          queryClient.invalidateQueries({ queryKey: [`all-meters-data-${diagnosisId}`] });
          // Invalida anche le query dei consumi per ricaricare la tabella dei costi
          queryClient.invalidateQueries({ queryKey: [`all-consumptions-${diagnosisId}`] });
          queryClient.invalidateQueries({ queryKey: [`all-consumption-data-${diagnosisId}`] });
          invalidateConsumptionTotalsQueries(queryClient, diagnosisId);
          toast.success('Dati contatori salvati con successo');
        },
        onError: (error: any) => {
          toast.error(error?.message || 'Errore nel salvataggio dei dati contatori');
        },
      }
    );

    const bulkDeleteMutation = useMutation(
      async (ids: string[]) => {
        for (const id of ids) {
          await apiClient.delete_item_diagnosis_meters__item_id__delete(id);
        }
        return ids;
      },
      {
        onSuccess: (ids: string[]) => {
          queryClient.invalidateQueries(`diagnosis-meters-${diagnosisId}`);
          queryClient.invalidateQueries({ queryKey: [`all-meters-data-${diagnosisId}`] });
          queryClient.invalidateQueries({ queryKey: [`all-consumptions-${diagnosisId}`] });
          queryClient.invalidateQueries({ queryKey: [`all-consumption-data-${diagnosisId}`] });
          invalidateConsumptionTotalsQueries(queryClient, diagnosisId);
          setSelectedMeterIds(prev => prev.filter(id => !ids.includes(id)));
          setBulkDeleteDialog(null);
          toast.success(
            ids.length === 1
              ? 'Contatore eliminato con successo'
              : `${ids.length} contatori eliminati con successo`
          );
        },
        onError: (error: any) => {
          toast.error(error?.message || 'Eliminazione dei contatori non riuscita');
        },
      }
    );

    React.useImperativeHandle(ref, () => ({
      save: async () => {
        if (!crossEntityGridRef.current) {
          return { success: true };
        }

        // IMPORTANTE: leggi prima i dati della tabella e solo dopo salva l'ordinamento.
        // `updateOrderMutation` invalida la query dei contatori: se l'ordine venisse salvato
        // per primo, il refetch potrebbe ricalcolare `sortedMeters`/le righe della griglia
        // prima che i dati non ancora salvati vengano letti, con il rischio di perderli
        // (causa principale della sparizione dei dati osservata dopo lo spostamento colonna).
        const tableData = crossEntityGridRef.current.getTableData();

        // Save order if changed (compare with original backend order)
        const originalOrder = meters.map(m => m.id!).filter(Boolean);
        const orderChanged =
          entityOrder.length === originalOrder.length &&
          entityOrder.some((id, index) => id !== originalOrder[index]);

        if (orderChanged) {
          try {
            await updateOrderMutation.mutateAsync(entityOrder);
          } catch (error) {
            // Error handled by mutation's onError
            return { success: false };
          }
        }

        if (tableData.length === 0) {
          return { success: true };
        }

        // Create a map of original values for comparison
        // Key: `${meter_id}-${month}`, Value: original value
        const originalValuesMap = new Map<string, number | null | undefined>();
        allMetersData.forEach(data => {
          if (data.meter_id && data.month) {
            const key = `${data.meter_id}-${dayjs(data.month).format('YYYY-MM')}`;
            originalValuesMap.set(key, data.value ?? (null as any));
          }
        });

        // Raccogli solo i dati modificati
        const itemsToSave: Array<MetersDataCreateDTO & { id?: string }> = [];

        for (const item of tableData) {
          // Normalize new value: convert to number or null
          const newValue =
            item.value !== null && item.value !== '' && item.value !== undefined
              ? Number(item.value)
              : null;

          // If this is an existing record, compare with original value
          if (item.valueId) {
            const key = `${item.entityId}-${dayjs(item.month).format('YYYY-MM')}`;
            const originalValue = originalValuesMap.get(key);

            // Normalize original value for comparison
            const normalizedOriginal =
              originalValue !== null && originalValue !== undefined ? Number(originalValue) : null;

            // Skip update if value hasn't changed (compare normalized values)
            if (normalizedOriginal === newValue) {
              continue; // Skip this item, no changes
            }
          }

          const createDto = new MetersDataCreateDTO({
            meter_id: item.entityId,
            month: toYyyyMmDd(item.month),
            value: newValue !== null ? newValue : (undefined as any),
          });

          const dtoWithId = createDto as MetersDataCreateDTO & { id?: string };
          if (item.valueId) {
            dtoWithId.id = item.valueId;
          }

          itemsToSave.push(dtoWithId);
        }

        // Perform batch upsert
        if (itemsToSave.length > 0) {
          await batchSaveMutation.mutateAsync(itemsToSave);
        }

        return { success: true };
      },
      add: async () => {
        if (!diagnosisId) {
          toast.error('Salva prima i dati principali della diagnosi.');
          return;
        }
        // Apri il dialog di modifica con un nuovo contatore
        setSelectedMeter(null);
        setEditDialogOpen(true);
      },
      actions: ['save', 'add'],
    }));

    const handleEditClick = (id: GridRowId) => {
      const meter = meters.find(m => m.id === id);
      if (meter) {
        setSelectedMeter(meter);
        setEditDialogOpen(true);
      }
    };

    const handleToggleMeterSelection = React.useCallback((id: string, checked: boolean) => {
      setSelectedMeterIds(prev =>
        checked ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter(itemId => itemId !== id)
      );
    }, []);

    const handleBulkDelete = () => {
      if (selectedMeterIds.length === 0) return;
      setBulkDeleteDialog([...selectedMeterIds]);
    };

    const handleConfirmBulkDelete = () => {
      if (!bulkDeleteDialog?.length) return;
      bulkDeleteMutation.mutate(bulkDeleteDialog);
    };

    // Prepara i dati per la tabella cross-entity
    const crossEntityValues: ValueWithEntityReference<MetersDataReadDTO>[] = React.useMemo(() => {
      return allMetersData.map(data => ({
        value: data,
        entityId: data.meter_id ?? '',
        month: data.month ?? new Date(),
      }));
    }, [allMetersData]);

    // Totali annui per contatore: somma dei valori mensili per anno (una sola colonna "Totale annuo")
    const meterYearlyValues = useMemo((): ValueWithEntityReference<{ value: number }>[] => {
      if (!crossEntityValues.length) return [];
      const fromYear = diagnosis?.data_generated_from
        ? dayjs(diagnosis.data_generated_from).year()
        : new Date().getFullYear();
      const toYear = diagnosis?.data_generated_to
        ? dayjs(diagnosis.data_generated_to).year()
        : fromYear;
      const years = new Set<number>();
      for (let y = fromYear; y <= toYear; y++) years.add(y);

      const sumByEntityYear = new Map<string, number>();
      crossEntityValues.forEach(item => {
        const year = dayjs(item.month).year();
        if (!years.has(year)) return;
        const key = `${item.entityId}-${year}`;
        const num = Number((item.value as any)?.value ?? 0);
        sumByEntityYear.set(key, (sumByEntityYear.get(key) ?? 0) + num);
      });

      const result: ValueWithEntityReference<{ value: number }>[] = [];
      meters.forEach(m => {
        const eid = m.id;
        if (!eid) return;
        years.forEach(year => {
          const key = `${eid}-${year}`;
          const sum = sumByEntityYear.get(key) ?? 0;
          result.push({
            entityId: eid,
            date: new Date(year, 0, 1),
            value: { value: sum },
          });
        });
      });
      return result;
    }, [crossEntityValues, meters, diagnosis?.data_generated_from, diagnosis?.data_generated_to]);

    // Totali per periodo di riferimento: solo mesi in [reference_period_from, reference_period_to]
    const meterReferencePeriodYearlyValues = useMemo((): ValueWithEntityReference<{
      value: number;
    }>[] => {
      if (!diagnosis?.reference_period_from || !diagnosis?.reference_period_to) return [];
      const entityIds = meters.map(m => m.id).filter(Boolean) as string[];
      return buildReferencePeriodYearlyValues({
        sourceValues: crossEntityValues as SourceValueItem[],
        periodFrom: diagnosis.reference_period_from,
        periodTo: diagnosis.reference_period_to,
        entityIds,
        buildValue: (_entityId, _year, sum, _count, _ctx) => ({ value: sum }),
      });
    }, [
      crossEntityValues,
      meters,
      diagnosis?.reference_period_from,
      diagnosis?.reference_period_to,
    ]);

    const meterYearlyValuesProps: ValuePropConfig<{ value: number }>[] = useMemo(
      () => [
        {
          field: 'value',
          label: 'Totale annuo',
          isPrimary: true,
          editable: false,
          getValue: v => (v != null ? Number((v as any).value) : null),
          setValue: v => v ?? { value: 0 },
        },
      ],
      []
    );

    // Posizione assoluta scroll orizzontale (valore unico per tutte e tre le tabelle)
    const [horizontalScrollLeft, setHorizontalScrollLeft] = React.useState(0);
    const setScrollPos = React.useCallback((pos: number) => {
      setHorizontalScrollLeft(prev => (prev === pos ? prev : pos));
    }, []);

    // Ordine colonne: stesso per la griglia mensile e le due tabelle annue, pilotato da
    // `entityOrder` (stato condiviso, aggiornato live quando l'utente sposta una colonna).
    const sortedMeters = useMemo(() => {
      if (!entityOrder.length) return meters;
      const byId = new Map(meters.map(m => [m.id, m]));
      const ordered = entityOrder.map(id => byId.get(id)).filter((m): m is DiagnosisMetersReadDTO => !!m);
      // Se l'ordine non è ancora stato riconciliato con l'elenco corrente (es. nel render
      // immediatamente successivo all'aggiunta/eliminazione di un contatore), usa `meters`
      // per non perdere colonne in visualizzazione: l'effetto di riconciliazione lo sistema
      // al render successivo.
      return ordered.length === meters.length ? ordered : meters;
    }, [meters, entityOrder]);

    // Callback quando una cella viene modificata
    const handleCellEditStop = () => {
      // Cell edit stopped
    };

    // Header personalizzato per la prima colonna (collapsed state)
    const dateColumnHeader = React.useMemo(() => {
      return (
        <Stack spacing={0.5} sx={{ fontSize: '0.75rem' }}>
          <Typography variant="caption" fontWeight={600}>
            Informazioni contatori
          </Typography>
        </Stack>
      );
    }, []);

    // Header personalizzato per la prima colonna (expanded state)
    const dateColumnHeaderExpanded = React.useMemo(() => {
      return (
        <Stack spacing={0.5} sx={{ fontSize: '0.75rem' }}>
          <Typography variant="caption" fontWeight={600} sx={{ pt: '0.2rem' }}>
            Informazioni contatori
          </Typography>
          <Typography variant="caption" fontWeight={600}>
            Vettore
          </Typography>
          <Typography variant="caption" fontWeight={600}>
            Unità di misura
          </Typography>
          <Typography variant="caption" fontWeight={600}>
            Tipo
          </Typography>
          <Typography variant="caption" fontWeight={600}>
            Codice
          </Typography>
          <Typography variant="caption" fontWeight={600}>
            ID contatore cliente
          </Typography>
          <Typography variant="caption" fontWeight={600}>
            Gerarchia
          </Typography>
          <Typography variant="caption" fontWeight={600}>
            Area funzionale
          </Typography>
          <Typography variant="caption" fontWeight={600}>
            Uso energetico
          </Typography>
          <Typography variant="caption" fontWeight={600}>
            Raggruppamento dato
          </Typography>
          <Typography variant="caption" fontWeight={600}>
            Note
          </Typography>
          <Box sx={{ height: '24px' }}></Box>
        </Stack>
      );
    }, []);

    // Header personalizzato per le colonne dei contatori (collapsed state)
    const renderMeterHeader = React.useCallback(
      (
        meter: DiagnosisMetersReadDTO,
        monthlyValues: { month: Date; value: number | string | null }[]
      ) => {
        const meterCode = meter.meter_code || '';
        const customerCode = (meter as any).meter_customer_code || '';
        const codeDisplay = customerCode ? `${meterCode}-${customerCode}` : meterCode;
        return (
          <Box sx={{ my: '4px' }}>
            <Typography
              variant="caption"
              fontWeight={600}
              noWrap
              title={codeDisplay}
              sx={{ textAlign: 'center' }}
            >
              {codeDisplay || '-'}{' '}
              {meter.unit_of_measurement &&
                `[${formatUnitOfMeasurement(meter.unit_of_measurement)}]`}
            </Typography>
          </Box>
        );
      },
      []
    );

    // Header personalizzato per le colonne dei contatori (expanded state)
    const renderMeterHeaderExpanded = React.useCallback(
      (
        meter: DiagnosisMetersReadDTO,
        monthlyValues: { month: Date; value: number | string | null }[]
      ) => {
        const energyVector = energyVectors.find(v => v.id === meter.energy_vector_id);
        // Trova il vettore energetico generico corrispondente per ottenere unit_of_measurement
        const genericVector = genericEnergyVectors.find(
          gv => gv.code === energyVector?.code || gv.vector_type === energyVector?.vector_type
        );

        const vectorType = energyVector?.vector_type || genericVector?.vector_type;
        const vectorName = vectorType
          ? vectorTypeOptions.find(opt => opt.value === vectorType)?.label || vectorType
          : (energyVector?.code ?? energyVector?.table_code_index ?? '-');

        return (
          <Stack spacing={0.5} sx={{ fontSize: '0.7rem', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Box sx={{ cursor: 'pointer' }} onClick={() => handleEditClick(meter.id as any)}>
                <EditIcon fontSize="small" />
              </Box>
              {meter.id && (
                <Checkbox
                  size="small"
                  checked={selectedMeterIds.includes(meter.id)}
                  onChange={e => {
                    e.stopPropagation();
                    handleToggleMeterSelection(meter.id!, e.target.checked);
                  }}
                  onClick={e => e.stopPropagation()}
                  sx={{ p: 0 }}
                />
              )}
            </Box>
            <Typography variant="caption" fontWeight={600} noWrap>
              {vectorName}
            </Typography>
            <Typography variant="caption" fontWeight={400} noWrap>
              {formatUnitOfMeasurement(meter.unit_of_measurement) || '-'}
            </Typography>
            <Typography variant="caption" fontWeight={400} noWrap>
              {(() => {
                const meterType = (meter as any).meter_type as string;
                if (!meterType) return '-';
                if (meterType === MeterType.PODp || meterType === 'PODp') return 'PODp (Prelievo)';
                if (meterType === MeterType.PODi || meterType === 'PODi')
                  return 'PODi (Immissione)';
                if (meterType === MeterType.PROD || meterType === 'PROD')
                  return 'PROD (Autoproduzione)';
                if (meterType === MeterType.PDR || meterType === 'PDR') return 'PDR (Gas)';
                if (meterType === MeterType.TOT || meterType === 'TOT') return 'TOT (Altro)';
                return meterType;
              })()}
            </Typography>
            <Typography variant="caption" fontWeight={400} noWrap title={meter.meter_code}>
              {meter.meter_code || '-'}
            </Typography>
            <Typography
              variant="caption"
              fontWeight={400}
              noWrap
              title={(meter as any).meter_customer_code}
            >
              {(meter as any).meter_customer_code || '-'}
            </Typography>
            <Typography variant="caption" fontWeight={400} noWrap>
              {meter.hierarchy || '-'}
            </Typography>
            <Typography variant="caption" fontWeight={400} noWrap>
              {meter.functional_area ? getFunctionalAreaLabel(meter.functional_area) : '-'}
            </Typography>
            <Typography variant="caption" fontWeight={400} noWrap>
              {(() => {
                const energyUseId = (meter as any).energy_use_id;
                if (!energyUseId) return '-';
                const energyUse = energyUses.find(eu => eu.id === energyUseId);
                return energyUse?.code || '-';
              })()}
            </Typography>
            <Typography variant="caption" fontWeight={400} noWrap>
              {meter.grouping || '-'}
            </Typography>
            <Typography variant="caption" fontWeight={400} noWrap title={meter.notes}>
              {meter.notes || '-'}
            </Typography>
          </Stack>
        );
      },
      [energyVectors, genericEnergyVectors, handleEditClick, handleToggleMeterSelection, energyUses, selectedMeterIds, vectorTypeOptions]
    );

    // Funzione per aprire il dialog di mapping
    const handlePaste = React.useCallback(async () => {
      if (!diagnosisId) {
        toast.error('Salva prima i dati principali della diagnosi.');
        return;
      }

      try {
        const text = await navigator.clipboard.readText();
        // Preserva righe con solo TAB (celle vuote): non filtrare per trim, così tab-tab = cella vuota
        let lines = text.split(/\r?\n/);
        if (lines.length > 0 && lines[lines.length - 1] === '') {
          lines = lines.slice(0, -1);
        }

        if (lines.length === 0) {
          toast.error('Nessun dato trovato nel clipboard');
          return;
        }

        // Parsa i dati del clipboard (split('\t') preserva celle vuote tra due tab)
        const clipboardData: string[][] = lines.map(line =>
          line.split('\t').map(col => col.trim())
        );

        // Apri il dialog con i dati
        setPasteMappingDialog({
          open: true,
          clipboardData,
        });
      } catch (error) {
        console.error('Errore durante il paste:', error);
        toast.error('Errore durante la lettura del clipboard');
      }
    }, [diagnosisId]);

    // Funzione per importare i dati con il mapping personalizzato
    const handleImportWithMapping = React.useCallback(
      async (mapping: Map<number, string>) => {
        if (!diagnosisId) {
          toast.error('Salva prima i dati principali della diagnosi.');
          return;
        }

        if (mapping.size === 0) {
          toast.error('Mappa almeno una riga a un campo');
          return;
        }

        const newMeters: any[] = [];
        const maxRows = pasteMappingDialog.clipboardData.length;
        const maxCols = Math.max(...pasteMappingDialog.clipboardData.map(row => row.length));
        const maxIndex =
          meters.length > 0 ? Math.max(...meters.map(m => (m as any).index ?? 0)) : 0;
        let nextOrderIndex = maxIndex + 1;

        // Per ogni colonna (contatore) nei dati originali
        for (let colIdx = 0; colIdx < maxCols; colIdx++) {
          const meterData: any = {
            diagnosis_id: diagnosisId,
            table_code_index: String(maxIndex + colIdx + 1),
            index: 0,
          };

          // Mappa le righe ai campi (ogni riga è una proprietà)
          mapping.forEach((fieldName, rowIndex) => {
            if (rowIndex >= maxRows) return;
            const value = pasteMappingDialog.clipboardData[rowIndex]?.[colIdx]?.trim();
            if (!value) return;

            if (fieldName === 'energy_vector_id') {
              // Cerca il vettore per tipo (es. "Energia elettrica", "Gas naturale", "Gasolio")
              // Prima cerca per match esatto del tipo
              let vector = energyVectors.find(v => {
                if (!v.vector_type) return false;
                // Confronta il tipo del vettore con il valore
                const vectorTypeLabel = vectorTypeOptions.find(
                  opt => opt.value === v.vector_type
                )?.label;
                return vectorTypeLabel === value || v.vector_type === value;
              });

              // Se non trovato, cerca per match parziale (case insensitive)
              if (!vector) {
                vector = energyVectors.find(v => {
                  if (!v.vector_type) return false;
                  const vectorTypeLabel = vectorTypeOptions.find(
                    opt => opt.value === v.vector_type
                  )?.label;
                  return (
                    vectorTypeLabel?.toLowerCase() === value.toLowerCase() ||
                    v.vector_type?.toLowerCase() === value.toLowerCase()
                  );
                });
              }

              // Se ancora non trovato, prova match parziale sulla label
              if (!vector) {
                vector = energyVectors.find(v => {
                  if (!v.vector_type) return false;
                  const vectorTypeLabel = vectorTypeOptions.find(
                    opt => opt.value === v.vector_type
                  )?.label;
                  return (
                    vectorTypeLabel?.toLowerCase().includes(value.toLowerCase()) ||
                    value.toLowerCase().includes(vectorTypeLabel?.toLowerCase() || '')
                  );
                });
              }

              if (vector) {
                meterData[fieldName] = vector.id;
              }
            } else if (fieldName === 'meter_code') {
              meterData[fieldName] = value;
            } else if (fieldName === 'meter_customer_code') {
              meterData[fieldName] = value;
            } else if (fieldName === 'unit_of_measurement') {
              meterData[fieldName] = value;
            } else if (fieldName === 'hierarchy') {
              meterData[fieldName] = value;
            } else if (fieldName === 'functional_area') {
              // Usa la funzione per convertire label -> enum
              const { getFunctionalAreaValueFromLabel } = require('../../../utils/functionalArea');
              const enumValue = getFunctionalAreaValueFromLabel(value);
              if (enumValue) {
                meterData[fieldName] = enumValue;
              }
            } else if (fieldName === 'energy_use_id') {
              // Cerca l'uso energetico per codice o descrizione
              let energyUse = energyUses.find(eu => eu.code === value);
              if (!energyUse) {
                energyUse = energyUses.find(
                  eu =>
                    eu.description === value ||
                    eu.description?.toLowerCase() === value.toLowerCase()
                );
              }
              if (energyUse) {
                meterData[fieldName] = energyUse.id;
              }
            } else if (fieldName === 'grouping') {
              meterData[fieldName] = value;
            } else if (fieldName === 'notes') {
              meterData[fieldName] = value;
            } else if (fieldName === 'type' || fieldName === 'meter_type') {
              meterData[fieldName] = value;
            }
          });

          // Se non c'è meter_code, salta questo contatore
          if (!meterData.meter_code) {
            continue;
          }

          meterData.index = nextOrderIndex;
          nextOrderIndex += 1;
          meterData.table_code_index = String(meterData.index);
          newMeters.push(meterData);
        }

        if (newMeters.length === 0) {
          toast.error('Nessun contatore valido trovato');
          return;
        }

        // Crea i contatori in parallelo; ricalcolo consumi una volta per vettore alla fine
        try {
          await Promise.all(
            newMeters.map(meterData =>
              apiClient.create_item_diagnosis_meters__post({
                ...meterData,
                skip_consumption_recalc: true,
              } as any)
            )
          );

          const vectorIds = Array.from(
            new Set(
              newMeters
                .map(m => m.energy_vector_id as string | undefined)
                .filter((id): id is string => Boolean(id))
            )
          );
          for (const vectorId of vectorIds) {
            await apiClient.calculate_consumption_diagnosis_consumption_calculate__energy_vector_id__post(
              vectorId
            );
          }

          queryClient.invalidateQueries(`diagnosis-meters-${diagnosisId}`);
          queryClient.invalidateQueries({ queryKey: [`all-consumptions-${diagnosisId}`] });
          queryClient.invalidateQueries({ queryKey: [`all-consumption-data-${diagnosisId}`] });
          setPasteMappingDialog({ open: false, clipboardData: [] });
          toast.success(`${newMeters.length} contatore/i aggiunto/i dalla clipboard`);
        } catch (error: any) {
          toast.error(error?.message || 'Errore durante la creazione dei contatori');
        }
      },
      [
        diagnosisId,
        meters,
        energyVectors,
        energyUses,
        pasteMappingDialog.clipboardData,
        queryClient,
      ]
    );

    // Campi per il mapping
    const meterFields: ColumnMappingField[] = React.useMemo(
      () => [
        { field: 'energy_vector_id', headerName: 'Vettore', width: 150 },
        { field: 'unit_of_measurement', headerName: 'UdM', width: 100 },
        { field: 'meter_code', headerName: 'Codice', width: 200 },
        { field: 'meter_customer_code', headerName: 'ID Cliente', width: 200 },
        { field: 'hierarchy', headerName: 'Gerarchia', width: 100 },
        { field: 'functional_area', headerName: 'Area funzionale', width: 150 },
        { field: 'energy_use_id', headerName: 'Uso energetico', width: 150 },
        { field: 'grouping', headerName: 'Raggruppamento dato', width: 150 },
        { field: 'notes', headerName: 'Note', width: 200 },
        { field: 'type', headerName: 'Tipo', width: 100 },
      ],
      []
    );

    if (!diagnosisId) {
      return (
        <Typography color="text.secondary">
          Salva le informazioni principali della diagnosi per gestire i contatori.
        </Typography>
      );
    }

    const hasGeneratedPeriod = diagnosis?.data_generated_from && diagnosis?.data_generated_to;

    if (!hasGeneratedPeriod) {
      return <Box sx={{ mt: 2 }}>Selezionare un periodo dati generali</Box>;
    }

    return (
      <>
        <Stack spacing={2} sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Contatori</Typography>
            {hasGeneratedPeriod && (
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Button onClick={handlePaste} variant="contained" startIcon={<ContentPasteIcon />}>
                  Incolla contatori
                </Button>
                <Button
                  onClick={handleBulkDelete}
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  disabled={selectedMeterIds.length === 0}
                >
                  Elimina selezionati ({selectedMeterIds.length})
                </Button>
              </Stack>
            )}
          </Stack>
          {hasGeneratedPeriod && (
            <Stack spacing={3} sx={{ mt: 3 }}>
              <Box sx={{ width: '100%' }}>
                <CrossEntityDateCrudDataGrid
                  ref={crossEntityGridRef}
                  periodFrom={diagnosis.data_generated_from!}
                  periodTo={diagnosis.data_generated_to!}
                  entities={sortedMeters}
                  values={crossEntityValues}
                  getEntityLabel={meter => meter.meter_code || meter.id || ''}
                  getValue={valueObj => valueObj.value ?? null}
                  setValue={(valueObj, newValue) => {
                    const updated = { ...valueObj };
                    updated.value =
                      newValue !== null && newValue !== '' ? Number(newValue) : undefined;
                    return updated as any;
                  }}
                  dateColumnHeader={dateColumnHeader}
                  dateColumnHeaderExpanded={dateColumnHeaderExpanded}
                  renderEntityHeader={renderMeterHeader}
                  renderEntityHeaderExpanded={renderMeterHeaderExpanded}
                  entityColumnWidth={120}
                  entityColumnWidthExpanded={200}
                  columnOrder={columnOrderFields}
                  onColumnOrderChange={handleColumnOrderChange}
                  onCellEditStop={handleCellEditStop}
                  readOnly={readOnly}
                  height="calc(100vh - 380px)"
                  loading={
                    isLoading ||
                    isLoadingMetersData ||
                    batchSaveMutation.isLoading ||
                    batchSaveMetersData.isLoading ||
                    updateOrderMutation.isLoading ||
                    bulkDeleteMutation.isLoading
                  }
                  enablePaste={true}
                  syncScrollLeft={horizontalScrollLeft}
                  onHorizontalScroll={setScrollPos}
                />
              </Box>
              <Typography variant="subtitle1">
                Dati di consumo energetico, di produzione e di costo - valori annui
              </Typography>
              <Box sx={{ width: '100%' }}>
                <CrossEntityDateCrudDataGrid
                  ref={crossEntityGridRefAnnual}
                  granularity="year"
                  periodFrom={diagnosis.data_generated_from!}
                  periodTo={diagnosis.data_generated_to!}
                  entities={sortedMeters}
                  values={meterYearlyValues}
                  valuesProps={meterYearlyValuesProps}
                  getEntityLabel={meter => meter.meter_code || meter.id || ''}
                  dateColumnHeader="Anno"
                  entityColumnWidth={120}
                  entityColumnWidthExpanded={200}
                  columnOrder={columnOrderFields}
                  onColumnOrderChange={handleColumnOrderChange}
                  renderEntityHeader={renderMeterHeader}
                  height={185}
                  loading={
                    isLoading ||
                    isLoadingMetersData ||
                    batchSaveMutation.isLoading ||
                    batchSaveMetersData.isLoading ||
                    updateOrderMutation.isLoading ||
                    bulkDeleteMutation.isLoading
                  }
                  readOnly={true}
                  disableHeaderExpand={true}
                  syncScrollLeft={horizontalScrollLeft}
                  onHorizontalScroll={setScrollPos}
                />
              </Box>
              <Typography variant="subtitle1">
                Periodo dati generati per la diagnosi energetica
              </Typography>
              <Box sx={{ width: '100%' }}>
                <CrossEntityDateCrudDataGrid
                  ref={crossEntityGridRefPeriodoRiferimento}
                  granularity="year"
                  periodFrom={diagnosis.reference_period_from!}
                  periodTo={diagnosis.reference_period_to!}
                  entities={sortedMeters}
                  values={meterReferencePeriodYearlyValues}
                  valuesProps={meterYearlyValuesProps}
                  getEntityLabel={meter => meter.meter_code || meter.id || ''}
                  dateColumnHeader="Anno"
                  entityColumnWidth={120}
                  entityColumnWidthExpanded={200}
                  columnOrder={columnOrderFields}
                  onColumnOrderChange={handleColumnOrderChange}
                  renderEntityHeader={renderMeterHeader}
                  height={85}
                  loading={
                    isLoading ||
                    isLoadingMetersData ||
                    batchSaveMutation.isLoading ||
                    batchSaveMetersData.isLoading ||
                    updateOrderMutation.isLoading ||
                    bulkDeleteMutation.isLoading
                  }
                  readOnly={true}
                  disableHeaderExpand={true}
                  syncScrollLeft={horizontalScrollLeft}
                  onHorizontalScroll={setScrollPos}
                />
              </Box>
            </Stack>
          )}
          <ActionSpacer></ActionSpacer>
        </Stack>

        <EditMeterDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedMeter(null);
          }}
          meter={selectedMeter}
          diagnosisId={diagnosisId}
          existingMeters={meters}
          genericEnergyVectors={genericEnergyVectors}
        />

        <ConfirmDialog
          open={!!bulkDeleteDialog?.length}
          onCancel={() => setBulkDeleteDialog(null)}
          onConfirm={handleConfirmBulkDelete}
          title="Conferma eliminazione"
          content={
            bulkDeleteDialog
              ? `Sei sicuro di voler eliminare ${bulkDeleteDialog.length} contatore/i selezionato/i?`
              : ''
          }
          confirmText="Elimina"
          cancelText="Annulla"
          loading={bulkDeleteMutation.isLoading}
        />

        {/* Dialog per mapping righe clipboard */}
        <ColumnMappingDialog
          open={pasteMappingDialog.open}
          onClose={() => setPasteMappingDialog({ open: false, clipboardData: [] })}
          onImport={handleImportWithMapping}
          clipboardData={pasteMappingDialog.clipboardData}
          fields={meterFields}
          dataOrientation="row"
        />
      </>
    );
  })
);

DiagnosisMetersStep.displayName = 'DiagnosisMetersStep';
